import { useState, useEffect, useCallback, useRef } from 'react';
import './ImageGalleryPicker.css';

// Allowed image extensions — must match server/index.js ALLOWED_IMAGE_EXTENSIONS
const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']);

// Derive alt text from path: /images/block1.png → "block1"
function getAlt(imgPath) {
    if (typeof imgPath !== 'string') return 'image';
    return imgPath.split('/').pop().replace(/\.[^.]+$/, '') || 'image';
}

// Normalize a path for comparison (lowercase, trim)
function normalizePath(p) {
    return typeof p === 'string' ? p.trim().toLowerCase() : '';
}

function ImageGalleryPicker({ currentValue = '', onSelect, fieldLabel = 'image' }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const gridRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchImages = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_URL}/api/images`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (!Array.isArray(data)) throw new Error('Invalid response from server');
            const validImages = data.filter(
                item => typeof item === 'string' && IMAGE_EXTENSIONS.has(
                    item.substring(item.lastIndexOf('.')).toLowerCase()
                )
            );
            setImages(validImages);
        } catch (err) {
            setError('Could not load images. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    // BUG-6 fix: guard onSelect
    const handleSelect = useCallback((imgPath) => {
        if (typeof onSelect === 'function') {
            onSelect(imgPath);
        }
    }, [onSelect]);

    // Clear the current image selection
    const handleClear = useCallback(() => {
        if (typeof onSelect === 'function') {
            onSelect('');
        }
    }, [onSelect]);

    // BUG-5 fix: keyboard navigation for the grid
    const handleKeyDown = useCallback((e, imgPath, idx) => {
        const buttons = gridRef.current?.querySelectorAll('.igp-thumb-btn');
        if (!buttons) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const next = buttons[idx + 1];
            if (next) next.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = buttons[idx - 1];
            if (prev) prev.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect(imgPath);
        }
    }, [handleSelect]);

    const normalizedCurrent = normalizePath(currentValue);
    const hasSelection = currentValue && currentValue.startsWith('/images/');

    if (loading) {
        return (
            <div className="igp-loading" role="status" aria-live="polite">
                <div className="igp-spinner" aria-hidden="true"></div>
                <span>Loading images…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="igp-error" role="alert">
                <p>{error}</p>
                <button type="button" onClick={fetchImages} className="igp-retry-btn">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="igp-container">
            {/* Gallery label — BUG Sally#1 fix */}
            <div className="igp-header">
                <span className="igp-label">Select {fieldLabel}</span>
                {hasSelection && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="igp-clear-btn"
                        title="Remove current image selection"
                    >
                        ✕ Clear
                    </button>
                )}
            </div>

            {images.length === 0 ? (
                <p className="igp-empty">
                    No images uploaded yet.{' '}
                    <a href="/admin/media" className="igp-media-link">Upload images in Media Manager</a>.
                </p>
            ) : (
                <>
                    {/* BUG-4/5 fix: use role="group" instead of role="listbox" to avoid
                        the invalid role="option" on <button> and mandatory arrow-key contract.
                        Keyboard navigation is implemented manually above. */}
                    <div
                        className="igp-grid"
                        role="group"
                        aria-label={`Image gallery — select a ${fieldLabel}`}
                        ref={gridRef}
                    >
                        {images.map((imgPath, idx) => {
                            const isSelected = normalizePath(imgPath) === normalizedCurrent;
                            return (
                                <button
                                    key={imgPath}
                                    type="button"
                                    aria-pressed={isSelected}
                                    aria-label={`${isSelected ? 'Selected: ' : 'Select: '}${getAlt(imgPath)}`}
                                    className={`igp-thumb-btn${isSelected ? ' selected' : ''}`}
                                    onClick={() => handleSelect(imgPath)}
                                    onKeyDown={(e) => handleKeyDown(e, imgPath, idx)}
                                    title={getAlt(imgPath)}
                                >
                                    <img
                                        src={`${API_URL}${imgPath}`}
                                        alt={getAlt(imgPath)}
                                        className="igp-thumb-img"
                                        loading="lazy"
                                    />
                                    {isSelected && (
                                        <span className="igp-selected-badge" aria-hidden="true">✓</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {/* BUG Sally#7 fix: scroll hint */}
                    {images.length > 12 && (
                        <p className="igp-scroll-hint" aria-hidden="true">
                            Scroll to see all {images.length} images
                        </p>
                    )}
                </>
            )}

            <button type="button" onClick={fetchImages} className="igp-refresh-btn">
                ↻ Refresh gallery
            </button>
        </div>
    );
}

export default ImageGalleryPicker;
